import { Button as MUIButton, ButtonProps, CircularProgress, SxProps } from '@mui/material';
import { COLORS } from '../../theme';
import Typography from '../Typography';
import { ChevronDownIcon, ChevronUpIcon, ChevronRightIcon } from '../../Icons';

export enum ButtonSizes {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export type ButtonType = 'fill' | 'outline' | 'link' | 'text';
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'white'
  | 'blue'
  | 'orange'
  | 'indigo';
type ButtonColorsPalette = {
  _default: string;
  hover: string;
  active: string;
};

type IButtonProps = Omit<ButtonProps, 'type' | 'color'> & {
  type?: ButtonType;
  color?: ButtonColor;
  isSubmit?: boolean;
  isLoading?: boolean;
  isFullHeight?: boolean;
  endIcon?: string | React.ReactNode;
  isTextWrap?: boolean;
};

const bgColorsMap: Record<ButtonColor, ButtonColorsPalette> = {
  primary: { _default: COLORS.green[600], hover: COLORS.brand.green, active: COLORS.green[700] },
  secondary: { _default: COLORS.indigo[600], hover: COLORS.indigo[400], active: COLORS.indigo[700] },
  tertiary: { _default: COLORS.gray[1300], hover: COLORS.gray[1100], active: COLORS.gray[1400] },
  danger: {
    _default: COLORS.feedback.danger,
    hover: COLORS.feedback.lightDanger,
    active: COLORS.feedback.danger,
  },
  white: {
    _default: COLORS.brand.white,
    hover: COLORS.brand.white,
    active: COLORS.brand.white,
  },
  blue: {
    _default: COLORS.cian[600],
    hover: COLORS.cian[550],
    active: COLORS.cian[650],
  },
  orange: {
    _default: COLORS.orange[900],
    hover: COLORS.orange[700],
    active: COLORS.orange[800],
  },
  indigo: {
    _default: COLORS.indigo[500],
    hover: COLORS.indigo[400],
    active: COLORS.indigo[700],
  },
};

const endIconMap = {
  arrowRight: <ChevronRightIcon />,
  arrowDown: <ChevronDownIcon />,
  arrowUp: <ChevronUpIcon />,
};

function getTextColor(type: ButtonType, color: ButtonColor): string | null {
  if (type === 'fill') {
    if (color === 'white') return COLORS.brand.night;
    return COLORS.brand.paper;
  }
  if (color === 'white') return COLORS.brand.night;
  return null;
}

const variantsMap: Record<ButtonType, ButtonProps['variant']> = {
  fill: 'contained',
  outline: 'outlined',
  link: 'text',
  text: 'text',
};

function getSxPropsForType(type: ButtonType, color: ButtonColor) {
  const { _default, hover, active } = bgColorsMap[color];
  const textColor = getTextColor(type, color) || _default;

  switch (type) {
    case 'fill':
      return {
        backgroundColor: _default,
        color: textColor,
        '&:hover': {
          backgroundColor: hover,
        },
        '&:active': {
          backgroundColor: active,
        },
        '&:disabled': {
          backgroundColor: COLORS.gray[400],
          color: COLORS.gray[1000],
        },
      };
    case 'outline':
      return {
        border: `1px solid ${textColor}`,
        color: textColor,
        '&:hover': {
          backgroundColor: 'initial',
          borderColor: hover,
          color: hover,
        },
        '&:active': {
          backgroundColor: 'initial',
          borderColor: active,
          color: active,
        },
        '&:disabled': {
          backgroundColor: COLORS.gray[400],
          color: COLORS.gray[1000],
        },
      };
    case 'text':
      return {
        color: textColor,
        '&:hover': {
          backgroundColor: 'initial',
          color: hover,
        },
        '&:active': {
          backgroundColor: 'initial',
          color: active,
        },
        p: '8px 0px',
      };
    case 'link':
      return {
        color: textColor,
        '&:hover': {
          backgroundColor: 'initial',
          color: hover,
        },
        '&:active': {
          backgroundColor: 'initial',
          color: active,
        },
        textDecoration: 'underline',
        p: '8px 0px',
      };
  }
}

function getSxPropsForSize(size: ButtonProps['size']) {
  switch (size) {
    case 'small':
      return {
        padding: '4px 12px',
        fontSize: 14,
      };
    case 'medium':
      return {
        padding: '12px 24px',
        fontSize: 16,
      };
    case 'large':
      return {
        padding: '16px 32px',
        fontSize: 18,
      };
  }
}

export default function Button({
  type = 'fill',
  color = 'primary',
  disabled = false,
  size = 'medium',
  children,
  isSubmit = false,
  fullWidth = false,
  isLoading = false,
  endIcon = null,
  isFullHeight = true,
  isTextWrap = true,
  ...rest
}: IButtonProps) {
  const sx: SxProps = {
    textTransform: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    padding: '12px 24px',
    height: isFullHeight ? '100%' : 'auto',
    ...getSxPropsForSize(size),
    ...getSxPropsForType(type, color),
  };
  const _endIcon = typeof endIcon === 'string' ? endIconMap[endIcon as keyof typeof endIconMap] : endIcon;

  return (
    <MUIButton
      variant={variantsMap[type]}
      disabled={disabled || isLoading}
      sx={{ ...sx, position: 'relative' }}
      disableElevation
      fullWidth={fullWidth}
      {...rest}
      endIcon={!isLoading && _endIcon}
      type={isSubmit ? 'submit' : 'button'}
    >
      <Typography
        sx={{
          color: 'inherit',
          fontWeight: 600,
          textWrap: isTextWrap ? 'wrap' : 'nowrap',
          visibility: isLoading ? 'hidden' : 'initial',
        }}
      >
        {children}
      </Typography>
      <CircularProgress
        size={25}
        sx={{ color: 'inherit', position: 'absolute', visibility: isLoading ? 'initial' : 'hidden' }}
      />
    </MUIButton>
  );
}
